import React, { FC, ReactElement, useMemo } from 'react';
import { CarouselRef } from 'antd/lib/carousel';
import { useTranslation } from 'react-i18next';
import { GroupCont, HeaderTitle, SliderHeader } from './ReportPRMGroupProportion.styles';
import PageSection from '../../HOC/PageSection/PageSection';
import { useFilters } from '../../hooks/useFilters';
import usePerformanceRiskMonitor from '../../hooks/usePerformanceRiskMonitor';
import { IPrmAnalysisResults } from '../../services/reports';
import PRMSliderBody from '../PRMSliderBody/PRMSliderBody';
import Slider from '../Slider/Slider';

interface IReportPRMGroupProportion {
  carouselRef: React.RefObject<CarouselRef> | null;
  prmResults: IPrmAnalysisResults;
  goToSlide: (slideNr: number) => void;
  group: string;
}

const ReportPRMGroupProportion: FC<IReportPRMGroupProportion> = ({
  carouselRef,
  prmResults,
  goToSlide,
  group,
}) => {
  const { currentFilters, applyAndMergeFilters } = useFilters();
  const { prmTransformedData } = usePerformanceRiskMonitor(prmResults.segmentResults || []);
  const { t } = useTranslation();

  const prmFilters = useMemo(() => {
    const prm = currentFilters?.prm;

    if (prm) {
      // @ts-expect-error query-string types a value as string | string[]
      const prmFilters = JSON.parse(prm);

      return {
        slide: prmFilters?.slide || 0,
        groupProportion: prmFilters?.groupProportion || {},
      };
    }
  }, [currentFilters?.prm]);

  const activeSlideFromUrl = prmFilters?.slide || 0;
  const activeExpandFromUrl = prmFilters?.groupProportion?.expand || '';
  const slideContTitle = prmTransformedData
    ? prmTransformedData[activeSlideFromUrl]?.segment?.title
    : '';

  const averages = useMemo(
    () =>
      prmTransformedData.reduce((acc: Record<string, number>, data, index) => {
        return data.workEnvironment.categoryResults.reduce((acc, categoryResult) => {
          const code = categoryResult.category.code;
          const score = categoryResult.result.score;
          const isLastResult = index === prmTransformedData.length - 1;

          if (isLastResult) {
            const average = Math.round(((acc[code] || 0) + score) / prmTransformedData.length);
            return { ...acc, [code]: average };
          }

          const sum = (acc[code] || 0) + score;
          return { ...acc, [code]: sum };
        }, acc);
      }, {}),
    [prmTransformedData]
  );

  const renderSlides = useMemo<ReactElement[]>(
    () =>
      prmTransformedData.map(result => (
        <PRMSliderBody
          key={result.segment.code}
          averages={averages}
          data={result}
          group={group}
          segmentCategory={{
            active: activeExpandFromUrl,
            setActive: (activeExpand: string) =>
              applyAndMergeFilters('prm', 'groupProportion', { expand: activeExpand }),
          }}
          donutPartAction={(slideNr: number) => goToSlide(slideNr)}
        />
      )),
    [activeExpandFromUrl, prmTransformedData, applyAndMergeFilters]
  );

  return (
    <PageSection title={t('report.prm.prmGroupProportion')}>
      <>
        <SliderHeader id="SliderHeader">
          <HeaderTitle id="HeaderTitle" data-testid="slideTitle">
            {slideContTitle}
          </HeaderTitle>
        </SliderHeader>
        <GroupCont id="GroupCont">
          <Slider
            slidesLength={prmTransformedData.length}
            activeSlide={activeSlideFromUrl}
            goToSlide={goToSlide}
            carouselRef={carouselRef}
          >
            {renderSlides as unknown as ReactElement}
          </Slider>
        </GroupCont>
      </>
    </PageSection>
  );
};

export default ReportPRMGroupProportion;
